// The sales pipeline's server state (spec/14 §16, F-HQ-39), as TanStack Query hooks over the generated client —
// `packages/contracts/src/routes/hq-leads.ts`. Every route needs `hq:leads` and a second factor; the server checks both.
//
//   listHqLeads            useLeadList(filters)          the pipeline, a page at a time
//   createHqLead           useCreateLead()
//   importHqLeads          useImportLeads()              dry run first; only a real import refreshes anything
//   listHqLeadCities       useLeadCities()               cities with counts, for the filter chips
//   getHqLeadQueue         useLeadQueue()                Today: follow-ups due, new leads to contact
//   getHqLeadFunnel        useLeadFunnel()
//   listHqLeadAssignees    useLeadAssignees()            who can own a lead
//   getHqLead              useLead(leadId)
//   updateHqLead           useUpdateLead()               { leadId, body }
//   deleteHqLead           useDeleteLead()               leadId
//   changeHqLeadStage      useChangeLeadStage()          { leadId, body }
//   listHqLeadActivities   useLeadActivities(leadId)     the timeline, a page at a time
//   logHqLeadActivity      useLogLeadActivity()          { leadId, body }
//   openHqLeadOutreach     useOpenLeadOutreach()         { leadId, body } → the link to open
//   convertHqLead          useConvertLead()              { leadId, body }
//   listHqLeadTemplates    useLeadTemplates(includeArchived)
//   createHqLeadTemplate   useCreateLeadTemplate()
//   updateHqLeadTemplate   useUpdateLeadTemplate()       { templateId, body }
//   archiveHqLeadTemplate  useArchiveLeadTemplate()      templateId
//
// A write refreshes what it can have changed: the lead, every list, Today's queue, the funnel, the cities and the
// timeline. The mutations take the lead's id as a variable, not as an argument, so one hook serves a board of cards.

import type {
  ApiClient,
  ChangeLeadStage,
  ConvertedLead,
  ConvertLead,
  CreateLead,
  CreateLeadTemplate,
  ImportLeads,
  Lead,
  LeadActivity,
  LeadActivityList,
  LeadAssigneeList,
  LeadCityList,
  LeadFunnel,
  LeadImportResult,
  LeadList,
  LeadOutreach,
  LeadQueue,
  LeadTemplate,
  LeadTemplateList,
  LogLeadActivity,
  OpenLeadOutreach,
  UpdateLead,
  UpdateLeadTemplate,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { organizationKeys } from '../organizations/queries';

/** One row of the pipeline. */
export type LeadSummary = LeadList['data'][number];

/** A page of the pipeline. */
export const LEAD_PAGE_SIZE = 50;

// -------------------------------------------------------------------------------------------------
// Filters (the pipeline's address)
// -------------------------------------------------------------------------------------------------

export const LEAD_STAGE_FILTERS = ['open', 'new', 'contacted', 'replied', 'demo', 'proposal', 'won', 'lost', 'parked'] as const;
export type LeadStageFilter = (typeof LEAD_STAGE_FILTERS)[number];
export const LEAD_PRIORITY_FILTERS = ['a', 'b', 'c'] as const;
export const LEAD_SORTS = ['followUp', 'reviews', 'updated'] as const;
export type LeadSort = (typeof LEAD_SORTS)[number];

/** The filters of the pipeline, exactly what `GET /v1/hq/leads` takes (less the paging). */
export interface LeadFilters {
  readonly stage?: LeadStageFilter;
  readonly city?: string;
  readonly priority?: (typeof LEAD_PRIORITY_FILTERS)[number];
  readonly ownerId?: string;
  readonly doNotContact?: 'true' | 'false';
  readonly q?: string;
  readonly sort?: LeadSort;
}

const text = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined);
const USER_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The filters an address carries, kept only where the api would take them. */
export function leadFiltersFrom(search: Record<string, unknown>): LeadFilters {
  const filters: { -readonly [K in keyof LeadFilters]: LeadFilters[K] } = {};
  const stage = text(search['stage']);
  if (stage !== undefined && (LEAD_STAGE_FILTERS as readonly string[]).includes(stage)) filters.stage = stage as LeadStageFilter;
  const city = text(search['city']);
  if (city !== undefined && city.length <= 80) filters.city = city;
  const priority = text(search['priority']);
  if (priority !== undefined && (LEAD_PRIORITY_FILTERS as readonly string[]).includes(priority)) filters.priority = priority as 'a' | 'b' | 'c';
  const ownerId = text(search['ownerId']);
  if (ownerId !== undefined && USER_ID.test(ownerId)) filters.ownerId = ownerId.toLowerCase();
  const doNotContact = text(search['doNotContact']);
  if (doNotContact === 'true' || doNotContact === 'false') filters.doNotContact = doNotContact;
  // The router reads a bare number in the address (`?q=7710076437`) as a number, not text: a phone search is exactly that.
  const rawQ = search['q'];
  const q = typeof rawQ === 'number' && Number.isSafeInteger(rawQ) && rawQ >= 0 ? String(rawQ) : text(rawQ);
  if (q !== undefined && q.length <= 120) filters.q = q;
  const sort = text(search['sort']);
  if (sort !== undefined && (LEAD_SORTS as readonly string[]).includes(sort) && sort !== 'followUp') filters.sort = sort as LeadSort;
  return filters;
}

// -------------------------------------------------------------------------------------------------
// Keys
// -------------------------------------------------------------------------------------------------

export const leadKeys = {
  all: ['hq', 'leads'] as const,
  /** Every list of the pipeline, whatever its filters. */
  lists: ['hq', 'leads', 'list'] as const,
  list: (filters: LeadFilters) => ['hq', 'leads', 'list', filters] as const,
  one: (leadId: string) => ['hq', 'leads', 'one', leadId] as const,
  activities: (leadId: string) => ['hq', 'leads', 'activities', leadId] as const,
  queue: ['hq', 'leads', 'queue'] as const,
  funnel: ['hq', 'leads', 'funnel'] as const,
  cities: ['hq', 'leads', 'cities'] as const,
  assignees: ['hq', 'leads', 'assignees'] as const,
  templates: ['hq', 'leads', 'templates'] as const,
  templateList: (includeArchived: boolean) => ['hq', 'leads', 'templates', includeArchived] as const,
};

/** The params of a lead's page, as HQ's links name them (plain strings: see `src/hq/paths.ts`). */
export const leadParams = (leadId: string): Readonly<Record<string, string>> => ({ leadId });

// The lead mutations below do not wait for the refetch: a dialog closes the moment the write is answered, and the lists, Today and
// the funnel catch up behind it (the lead itself is set from the answer, so its page is right at once).

/** Everything a lead's change can show up in: the lists, Today, the funnel, the cities, and the lead and its timeline. */
export async function refreshLeads(queryClient: QueryClient, leadId?: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: leadKeys.lists }),
    queryClient.invalidateQueries({ queryKey: leadKeys.queue }),
    queryClient.invalidateQueries({ queryKey: leadKeys.funnel }),
    queryClient.invalidateQueries({ queryKey: leadKeys.cities }),
    ...(leadId === undefined
      ? []
      : [queryClient.invalidateQueries({ queryKey: leadKeys.one(leadId) }), queryClient.invalidateQueries({ queryKey: leadKeys.activities(leadId) })]),
  ]);
}

// -------------------------------------------------------------------------------------------------
// Reads
// -------------------------------------------------------------------------------------------------

export function listLeadsPage(api: ApiClient, filters: LeadFilters, cursor: string | undefined): Promise<LeadList> {
  return api.listHqLeads({ query: { limit: LEAD_PAGE_SIZE, ...filters, ...(cursor === undefined ? {} : { cursor }) } });
}

/** The pipeline, filtered and sorted, a page at a time. */
export function useLeadList(filters: LeadFilters, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: leadKeys.list(filters),
    queryFn: ({ pageParam }) => listLeadsPage(api, filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    enabled,
  });
}

/** One lead, in full. */
export function useLead(leadId: string) {
  const api = useApi();
  return useQuery<Lead>({ queryKey: leadKeys.one(leadId), queryFn: () => api.getHqLead({ path: { leadId } }) });
}

/** A lead's timeline, newest first, a page at a time. */
export function useLeadActivities(leadId: string) {
  const api = useApi();
  return useInfiniteQuery<LeadActivityList, Error, { pages: LeadActivityList[] }, readonly string[], string | undefined>({
    queryKey: leadKeys.activities(leadId),
    queryFn: ({ pageParam }) => api.listHqLeadActivities({ path: { leadId }, query: { limit: LEAD_PAGE_SIZE, ...(pageParam === undefined ? {} : { cursor: pageParam }) } }),
    initialPageParam: undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
  });
}

/** Today: the follow-ups due, the new leads to contact, and how many first messages are left. */
export function useLeadQueue() {
  const api = useApi();
  return useQuery<LeadQueue>({ queryKey: leadKeys.queue, queryFn: () => api.getHqLeadQueue({}) });
}

/** Leads by stage and city, and which template got replies. */
export function useLeadFunnel() {
  const api = useApi();
  return useQuery<LeadFunnel>({ queryKey: leadKeys.funnel, queryFn: () => api.getHqLeadFunnel({}) });
}

/** The cities leads are in, with counts, biggest first. */
export function useLeadCities() {
  const api = useApi();
  return useQuery<LeadCityList>({ queryKey: leadKeys.cities, queryFn: () => api.listHqLeadCities({}) });
}

/** The HQ members who can own a lead. */
export function useLeadAssignees() {
  const api = useApi();
  return useQuery<LeadAssigneeList>({ queryKey: leadKeys.assignees, queryFn: () => api.listHqLeadAssignees({}), staleTime: 60_000 });
}

/** The message templates, the archived ones too when asked. */
export function useLeadTemplates(includeArchived = false, enabled = true) {
  const api = useApi();
  return useQuery<LeadTemplateList>({
    queryKey: leadKeys.templateList(includeArchived),
    queryFn: () => api.listHqLeadTemplates({ query: includeArchived ? { includeArchived: 'true' } : {} }),
    enabled,
  });
}

// -------------------------------------------------------------------------------------------------
// Writes
// -------------------------------------------------------------------------------------------------

/** Add a lead. `409 lead-phone-taken` when another lead has the number. */
export function useCreateLead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Lead, Error, CreateLead>({
    mutationFn: (body) => api.createHqLead({ body }),
    onSuccess: (lead) => {
      queryClient.setQueryData(leadKeys.one(lead.id), lead);
      void refreshLeads(queryClient);
    },
  });
}

/**
 * Import the sales spreadsheet. A dry run reads the file and writes nothing, so it refreshes nothing; the real import
 * refreshes every list, Today, the funnel and the cities.
 */
export function useImportLeads() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadImportResult, Error, ImportLeads>({
    mutationFn: (body) => api.importHqLeads({ body }),
    onSuccess: async (_result, body) => {
      if (!body.dryRun) await refreshLeads(queryClient);
    },
  });
}

/** Edit a lead; also sets and clears do-not-contact (the reason is required to set it). */
export function useUpdateLead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Lead, Error, { readonly leadId: string; readonly body: UpdateLead }>({
    mutationFn: ({ leadId, body }) => api.updateHqLead({ path: { leadId }, body }),
    onSuccess: (lead, { leadId }) => {
      queryClient.setQueryData(leadKeys.one(leadId), lead);
      void refreshLeads(queryClient, leadId);
    },
  });
}

/** Erase a lead with its timeline. */
export function useDeleteLead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (leadId) => api.deleteHqLead({ path: { leadId } }),
    onSuccess: async (_result, leadId) => {
      queryClient.removeQueries({ queryKey: leadKeys.one(leadId) });
      queryClient.removeQueries({ queryKey: leadKeys.activities(leadId) });
      await refreshLeads(queryClient);
    },
  });
}

/** Move a lead to another stage; `lost` needs a reason. */
export function useChangeLeadStage() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Lead, Error, { readonly leadId: string; readonly body: ChangeLeadStage }>({
    mutationFn: ({ leadId, body }) => api.changeHqLeadStage({ path: { leadId }, body }),
    onSuccess: (lead, { leadId }) => {
      queryClient.setQueryData(leadKeys.one(leadId), lead);
      void refreshLeads(queryClient, leadId);
    },
  });
}

/** Log a note, call, visit or reply on a lead's timeline. */
export function useLogLeadActivity() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadActivity, Error, { readonly leadId: string; readonly body: LogLeadActivity }>({
    mutationFn: ({ leadId, body }) => api.logHqLeadActivity({ path: { leadId }, body }),
    onSuccess: (_activity, { leadId }) => {
      void refreshLeads(queryClient, leadId);
    },
  });
}

/** Record that a message is being opened (HQ sends nothing) and get the link to open it from. */
export function useOpenLeadOutreach() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadOutreach, Error, { readonly leadId: string; readonly body: OpenLeadOutreach }>({
    mutationFn: ({ leadId, body }) => api.openHqLeadOutreach({ path: { leadId }, body }),
    onSuccess: (outreach, { leadId }) => {
      queryClient.setQueryData(leadKeys.one(leadId), outreach.lead);
      void refreshLeads(queryClient, leadId);
    },
  });
}

/** Make the lead an organisation (needs `hq:organizations` too); the organisations list is refreshed as well. */
export function useConvertLead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<ConvertedLead, Error, { readonly leadId: string; readonly body: ConvertLead }>({
    mutationFn: ({ leadId, body }) => api.convertHqLead({ path: { leadId }, body }),
    onSuccess: async (converted, { leadId }) => {
      queryClient.setQueryData(leadKeys.one(leadId), converted.lead);
      await Promise.all([refreshLeads(queryClient, leadId), queryClient.invalidateQueries({ queryKey: organizationKeys.list })]);
    },
  });
}

const refreshTemplates = (queryClient: QueryClient) => queryClient.invalidateQueries({ queryKey: leadKeys.templates });

export function useCreateLeadTemplate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadTemplate, Error, CreateLeadTemplate>({
    mutationFn: (body) => api.createHqLeadTemplate({ body }),
    onSuccess: () => refreshTemplates(queryClient),
  });
}

export function useUpdateLeadTemplate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadTemplate, Error, { readonly templateId: string; readonly body: UpdateLeadTemplate }>({
    mutationFn: ({ templateId, body }) => api.updateHqLeadTemplate({ path: { templateId }, body }),
    onSuccess: () => refreshTemplates(queryClient),
  });
}

/** Archive a template: it stops being offered, and the messages already sent with it keep their credit in the funnel. */
export function useArchiveLeadTemplate() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<LeadTemplate, Error, string>({
    mutationFn: (templateId) => api.archiveHqLeadTemplate({ path: { templateId } }),
    onSuccess: async () => {
      await refreshTemplates(queryClient);
      await queryClient.invalidateQueries({ queryKey: leadKeys.funnel });
    },
  });
}
